import { useState } from 'react'
import {
  TEMPLATE_CATALOG,
  TEMPLATE_CATEGORY_ORDER,
  type TemplateCategory,
} from '@shared/templates'
import { useApi } from '../contexts/ApiContext'

/** Short blurb per category shown under the group heading. */
const CATEGORY_BLURB: Record<TemplateCategory, string> = {
  Basic: 'Standard roster collection — hand these to a client at kickoff.',
  Advanced: 'Deeper HR data collection for full workforce-reinvention engagements.',
  Finance: 'Finance-specific data requests.',
  GCC: 'Global Capability Center data requests.',
}

export default function Templates() {
  const api = useApi()
  // Per-template download/error state so a failure on one card never blocks another.
  const [busyId, setBusyId] = useState<string | null>(null)
  const [errorId, setErrorId] = useState<string | null>(null)

  const download = async (id: string) => {
    setBusyId(id)
    setErrorId(null)
    try {
      await api.downloadTemplate(id)
    } catch {
      setErrorId(id)
    } finally {
      setBusyId(null)
    }
  }

  // Only render categories that actually have templates, in the fixed order.
  const categories = TEMPLATE_CATEGORY_ORDER.filter((cat) =>
    TEMPLATE_CATALOG.some((t) => t.category === cat)
  )

  return (
    <div className="p-8 max-w-3xl animate-fade-in">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Templates</h1>
      <p className="text-sm text-gray-500 mb-8">
        Download a pre-formatted Excel template to share with your client for data
        collection, then upload the completed file when you create a navigator.
      </p>

      {categories.map((category) => (
        <section key={category} className="mb-8">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-400 mb-1">
            {category}
          </h2>
          <p className="text-xs text-gray-400 mb-3">{CATEGORY_BLURB[category]}</p>

          <div className="space-y-3">
            {TEMPLATE_CATALOG.filter((t) => t.category === category).map((t) => (
              <div
                key={t.id}
                role="group"
                aria-label={t.name}
                className="flex items-start justify-between gap-4 p-4 bg-white rounded-xl border border-gray-100 shadow-sm"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <span className="text-xl leading-none mt-0.5">📊</span>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-gray-800">{t.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>
                    {errorId === t.id && (
                      <p className="text-xs text-red-500 mt-1.5">
                        Download failed — please try again.
                      </p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => void download(t.id)}
                  disabled={busyId === t.id}
                  className="flex-shrink-0 py-2 px-4 rounded-lg text-white text-sm font-semibold transition-all disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
                >
                  {busyId === t.id ? 'Downloading…' : '⬇ Download'}
                </button>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
